import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { FilterIcon, ChevronDownIcon, Download } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { getDefaultAcademicYearRange } from '../../../utils/dashboardDateRange';
import { createReportCsvBlob } from '../../../utils/reportCsvExport';
import { translateDeepestOrgPath } from '../../../utils/translateReportOrgPath';
import { getLearningLevelTranslation } from '../../../services/learningLevelsTranslationsService';
import { useReportOrgScope } from './useReportOrgScope';
import ReportOrgFilterFields from './ReportOrgFilterFields';
import {
  REPORT_FILTER_PANEL,
  reportFilterToggleClass,
  REPORT_INPUT,
  REPORT_LABEL,
  REPORT_THEAD_ROW,
  ReportTh,
  REPORT_TABLE_VALIGN
} from './ReportPageUi';
import ReportUsersCharts from './ReportUsersCharts';
import { buildUsersChartCsvAppend } from './usersReportChartsUtils';
import { useReportChartFetch, useReportChartDisplay } from './useReportChartFetch';

const ROLE_FILTER_VALUES = ['', 'super_admin', 'admin', 'student', 'trainer', 'supervisor', 'manager'];

/** خيارات حجم الصفحة؛ «الكل» يرسل pageSize=10000 كحد أقصى (مطابق لـ maxAll في الـ API). */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000];
const PAGE_SIZE_ALL = 10000;

/** يبني معاملات الطلب بدون قيم فارغة — كثير من واجهات الـ API ترفض query params الفارغة (400). */
function buildUsersReportParams(page, pageSize, applied, orgFilters) {
  const params = {
    page,
    pageSize: String(pageSize)
  };
  const search = (applied.search || '').trim();
  if (search) params.search = search;
  if (applied.dateFrom) params.dateFrom = applied.dateFrom;
  if (applied.dateTo) params.dateTo = applied.dateTo;
  if (applied.roleName) params.roleName = applied.roleName;
  if (orgFilters.countryId) params.countryId = orgFilters.countryId;
  if (orgFilters.regionId) params.regionId = orgFilters.regionId;
  if (orgFilters.academyId) params.academyId = orgFilters.academyId;
  if (orgFilters.districtId) params.districtId = orgFilters.districtId;
  if (orgFilters.institutionId) params.institutionId = orgFilters.institutionId;
  if (orgFilters.departmentId) params.departmentId = orgFilters.departmentId;
  return params;
}

function csvEscapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** حجم كل طلب أثناء التصدير (أخف على الخادم من 10000 دفعة واحدة + يقلل خطر انتهاء المهلة). */
const CSV_FETCH_PAGE_SIZE = 1000;

/** مهلة أطول من الإعداد الافتراضي لـ axios (10s) لأن استعلام التقرير قد يكون ثقيلاً. */
const CSV_EXPORT_TIMEOUT_MS = 180000;

/** يجلب كل الصفوف المطابقة للفلاتر عبر الترقيم — التوقف عند صف غير ممتلئ أو صف فارغ (لا نعتمد على totalPages فقط؛ قد يكون خاطئاً). */
async function fetchAllUsersReportRows(applied, orgFilters) {
  const rows = [];
  let pageNum = 1;
  const maxSafetyPages = 500;

  while (pageNum <= maxSafetyPages) {
    const params = buildUsersReportParams(pageNum, CSV_FETCH_PAGE_SIZE, applied, orgFilters);
    const response = await api.get('/reports/users', {
      params,
      timeout: CSV_EXPORT_TIMEOUT_MS
    });
    if (!response.data?.success) {
      const msg = response.data?.message || response.data?.error;
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg || 'export failed'));
    }
    const data = response.data.data || {};
    const batch = data.items || [];
    rows.push(...batch);

    if (batch.length === 0) break;
    if (batch.length < CSV_FETCH_PAGE_SIZE) break;

    pageNum += 1;
  }
  return rows;
}

const ReportUsers = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const org = useReportOrgScope(currentLanguage);

  const defaultRange = getDefaultAcademicYearRange();

  const [draft, setDraft] = useState({
    search: '',
    roleName: '',
    dateFrom: defaultRange.dateFrom,
    dateTo: defaultRange.dateTo
  });
  const [applied, setApplied] = useState({
    search: '',
    roleName: '',
    dateFrom: defaultRange.dateFrom,
    dateTo: defaultRange.dateTo
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 0 });
  const [hasScope, setHasScope] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildUsersReportParams(page, pageSize, applied, org.appliedFilters);
      const response = await api.get('/reports/users', { params });
      if (response.data.success) {
        const data = response.data.data || {};
        setHasScope(data.hasScope !== false);
        setItems(data.items || []);
        const p = data.pagination || {};
        setPagination({
          page: Number(p.page) || page,
          pageSize: Number(p.pageSize) || pageSize,
          total: Number(p.total) || 0,
          totalPages: Number(p.totalPages) || 0
        });
      } else {
        setItems([]);
        setPagination({ page: 1, pageSize, total: 0, totalPages: 0 });
      }
    } catch (error) {
      const data = error.response?.data;
      const serverMsg = data?.message || data?.error || data?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      if (detail) {
        toast.error(String(detail), { duration: 5000, position: 'top-center' });
      } else {
        toast.error(t('reports.fetchError'), { duration: 3000, position: 'top-center' });
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied, org.appliedFilters, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const chartFiltersKey = useMemo(
    () =>
      [
        applied.search,
        applied.dateFrom,
        applied.dateTo,
        applied.roleName,
        org.appliedFilters?.countryId,
        org.appliedFilters?.regionId,
        org.appliedFilters?.academyId,
        org.appliedFilters?.districtId,
        org.appliedFilters?.institutionId,
        org.appliedFilters?.departmentId
      ].join('|'),
    [applied, org.appliedFilters]
  );

  const { chartRows, chartLoading } = useReportChartFetch(
    chartFiltersKey,
    fetchAllUsersReportRows,
    applied,
    org.appliedFilters
  );
  const { rowsForCharts, chartsShowLoading, partial: chartsPartial } = useReportChartDisplay(
    chartRows,
    chartLoading,
    items,
    loading
  );

  const applyFiltersHandler = () => {
    org.applyOrgFilters();
    setApplied({ ...draft });
    setPage(1);
    org.setShowFilters(false);
  };

  const clearExtraFilters = () => {
    const r = getDefaultAcademicYearRange();
    setDraft((prev) => ({
      ...prev,
      search: '',
      roleName: '',
      dateFrom: r.dateFrom,
      dateTo: r.dateTo
    }));
    setApplied((prev) => ({
      ...prev,
      search: '',
      roleName: '',
      dateFrom: r.dateFrom,
      dateTo: r.dateTo
    }));
    setPage(1);
    org.setShowFilters(true);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(currentLanguage === 'ar' ? 'ar' : currentLanguage === 'fr' ? 'fr-FR' : 'en-US');
    } catch {
      return '—';
    }
  };

  const activeLabel = (v) => (v === 1 || v === true ? t('reports.users.activeYes') : t('reports.users.activeNo'));

  const langKey = currentLanguage === 'ar' ? 'ar' : currentLanguage === 'fr' ? 'fr' : 'en';

  const formatLevelLabel = useCallback(
    (row) => {
      if (row.soroban_level_name != null && String(row.soroban_level_name).trim() !== '') {
        const name = String(row.soroban_level_name).trim();
        const tr = getLearningLevelTranslation(name);
        if (tr && tr[langKey]) return tr[langKey];
        return name;
      }
      if (row.soroban_level_number != null) return String(row.soroban_level_number);
      return '—';
    },
    [langKey]
  );

  const formatOrgPathCell = (path) => {
    if (!path || !String(path).trim()) return '—';
    return translateDeepestOrgPath(String(path), currentLanguage);
  };

  const downloadCsv = async () => {
    if (exportingCsv || (pagination.total === 0 && items.length === 0)) return;
    setExportingCsv(true);
    try {
      const allRows = await fetchAllUsersReportRows(applied, org.appliedFilters);
      if (allRows.length === 0) {
        toast.error(t('reports.users.csvNoRows'), { position: 'top-center' });
        return;
      }
      const headers = [
        t('reports.users.colFullName'),
        t('reports.users.colUsername'),
        t('reports.users.colEmail'),
        t('reports.users.colPhone'),
        t('reports.users.colRegistered'),
        t('reports.users.colRole'),
        t('reports.users.colActive'),
        t('reports.users.colStudentNo'),
        t('reports.users.colSorobanLevel'),
        t('reports.users.colOrgPath')
      ];
      const lines = [headers.map(csvEscapeCell).join(',')];
      for (const row of allRows) {
        const line = [
          row.full_name || '',
          row.username || '',
          row.email || '',
          row.phone || '',
          formatDate(row.registration_date),
          row.roles || '',
          activeLabel(row.is_active),
          row.student_number || '',
          formatLevelLabel(row),
          formatOrgPathCell(row.deepest_org_path)
        ].map(csvEscapeCell).join(',');
        lines.push(line);
      }
      const chartCsv = buildUsersChartCsvAppend(allRows, formatLevelLabel, t);
      const csvBody = `${lines.join('\r\n')}${chartCsv}`;
      const blob = createReportCsvBlob(csvBody);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `users-report-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('reports.users.csvSuccess', { count: allRows.length }), { position: 'top-center' });
    } catch (err) {
      const res = err.response?.data;
      const serverMsg = res?.message ?? res?.error ?? res?.errors;
      const detail = Array.isArray(serverMsg)
        ? serverMsg.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e))).join(', ')
        : typeof serverMsg === 'object' && serverMsg !== null
          ? JSON.stringify(serverMsg)
          : serverMsg;
      const axiosMsg = err.code === 'ECONNABORTED' || err.message?.includes('timeout')
        ? t('reports.users.csvTimeout')
        : null;
      const fallback = t('reports.users.csvError');
      toast.error(axiosMsg || (detail ? String(detail) : err.message) || fallback, { duration: 6000, position: 'top-center' });
    } finally {
      setExportingCsv(false);
    }
  };

  const canExportCsv = !loading && !exportingCsv && (pagination.total > 0 || items.length > 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm shadow-slate-900/5 ring-1 ring-slate-900/[0.03]">
      <div className={REPORT_FILTER_PANEL}>
        <button
          type="button"
          onClick={() => org.setShowFilters(!org.showFilters)}
          className={reportFilterToggleClass(org.showFilters)}
        >
          <FilterIcon className="h-4 w-4 shrink-0 text-primary-600" />
          {t('common.filters')}
          <ChevronDownIcon className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${org.showFilters ? 'rotate-180' : ''}`} />
        </button>
        {org.showFilters && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div>
              <label className={REPORT_LABEL}>{t('reports.users.search')}</label>
              <input
                type="search"
                value={draft.search}
                onChange={(e) => setDraft((prev) => ({ ...prev, search: e.target.value }))}
                className={REPORT_INPUT}
                placeholder={t('reports.users.searchPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.users.role')}</label>
              <select
                value={draft.roleName}
                onChange={(e) => setDraft((prev) => ({ ...prev, roleName: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                {ROLE_FILTER_VALUES.map((val) => (
                  <option key={val || 'all'} value={val}>
                    {val ? t(`reports.users.role_${val}`) : t('reports.users.roleAll')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateFrom')}</label>
              <input
                type="date"
                value={draft.dateFrom}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateFrom: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.dateTo')}</label>
              <input
                type="date"
                value={draft.dateTo}
                onChange={(e) => setDraft((prev) => ({ ...prev, dateTo: e.target.value }))}
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <ReportOrgFilterFields
              filters={org.filters}
              setFilters={org.setFilters}
              lockedOrgLevels={org.lockedOrgLevels}
              countries={org.countries}
              regions={org.regions}
              academies={org.academies}
              districts={org.districts}
              institutions={org.institutions}
              departments={org.departments}
              getCountryLabel={(c) => org.getCountryTranslation(c)}
            />
          </div>
        )}
        {org.showFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={applyFiltersHandler}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <FilterIcon className="h-4 w-4" />
              {t('reports.applyFilters')}
            </button>
            <button
              type="button"
              onClick={clearExtraFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              {t('common.clearFilters')}
            </button>
          </div>
        )}
      </div>

      <div className="p-6">
        {!hasScope && (
          <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm mb-4">
            {t('reports.users.noScope')}
          </p>
        )}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-500 text-center py-12">{t('reports.noData')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
              <button
                type="button"
                onClick={downloadCsv}
                disabled={!canExportCsv}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                {exportingCsv ? t('reports.users.csvExporting') : t('reports.users.downloadCsv')}
              </button>
            </div>
            <ReportUsersCharts
              rows={rowsForCharts}
              loading={chartsShowLoading}
              partial={chartsPartial}
              formatLevelLabel={formatLevelLabel}
              t={t}
            />
            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="overflow-x-auto max-w-full">
                <table className={`min-w-[960px] w-full text-sm ${REPORT_TABLE_VALIGN}`}>
                  <thead>
                    <tr className={REPORT_THEAD_ROW}>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colFullName')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colUsername')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colEmail')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colPhone')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colRegistered')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colRole')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colActive')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colStudentNo')}</ReportTh>
                      <ReportTh className="!py-3 !px-3">{t('reports.users.colSorobanLevel')}</ReportTh>
                      <ReportTh className="!py-3 !px-3 min-w-[200px]">{t('reports.users.colOrgPath')}</ReportTh>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {items.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/80">
                        <td className="py-3 px-3 text-gray-900 font-medium">{row.full_name || '—'}</td>
                        <td className="py-3 px-3 text-gray-700">{row.username || '—'}</td>
                        <td className="py-3 px-3 text-gray-700 break-all max-w-[180px]">{row.email || '—'}</td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">{row.phone || '—'}</td>
                        <td className="py-3 px-3 text-gray-700 whitespace-nowrap">{formatDate(row.registration_date)}</td>
                        <td className="py-3 px-3 text-gray-700">{row.roles || '—'}</td>
                        <td className="py-3 px-3 text-gray-700">{activeLabel(row.is_active)}</td>
                        <td className="py-3 px-3 text-gray-700">{row.student_number || '—'}</td>
                        <td className="py-3 px-3 text-gray-700">{formatLevelLabel(row)}</td>
                        <td className="py-3 px-3 text-gray-600 text-xs leading-snug">{formatOrgPathCell(row.deepest_org_path)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
              <p>
                {t('reports.users.pageInfo', {
                  page: pagination.page,
                  totalPages: pagination.totalPages || 1,
                  total: pagination.total
                })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2">
                  <span>{t('reports.users.pageSize')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="rounded-md border border-gray-300 py-1.5 px-2 text-sm min-w-[10rem]"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    <option value={PAGE_SIZE_ALL}>{t('reports.users.pageSizeOptionAll')}</option>
                  </select>
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.prev')}
                  </button>
                  <button
                    type="button"
                    disabled={page >= (pagination.totalPages || 1)}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                  >
                    {t('reports.users.next')}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportUsers;
